import { Module } from "vuex";
import Notify from 'quasar/src/plugins/Notify.js';;
import i18n from "@/i18n";
import { logger } from "@/utils/logger";
import store from "@/store";
import {
  translationList,
  getTranslation,
  updateTranslation,
  createTranslation,
  deleteTranslation as deleteTranslationApi,
} from "@/store/services/translation/translationService";

/**
 * DB shape this mirrors (`odm-configuration`.configuration_translation).
 * The table also has a country_id column, but it's deliberately unused
 * here - see createTranslation() in translationService.ts.
 */
export interface TranslationRow {
  id: string;
  _key: string;
  _value: string;
  language_id: string;
  _daaq: string;
  _business_data: unknown;
  _when_created: string;
  _when_modified: string;
  _who_created: string;
  _who_modified: string;
}

export interface TranslationState {
  mode: "add" | "edit" | "view";
  selectedTranslation: Partial<TranslationRow>;
}

const translationModule: Module<TranslationState, any> = {
  namespaced: true,
  state: {
    mode: "add",
    selectedTranslation: {},
  },

  getters: {
    getTranslationSelectedInit() {
      return {
        id: "",
        _key: "",
        _value: "",
        language_id: "",
        _daaq: "/",
        _business_data: null,
      };
    },
  },

  actions: {
    setMode({ commit }, payload) {
      commit("setMode", payload);
    },

    initTranslationModule({ commit }) {
      commit("initTranslationModule");
    },

    /**
     * Mirrors userModule/searchUser's shape exactly: TranslationList.vue
     * calls searchCount() itself (via getQcTranslation in filterService.ts)
     * for the total row count, then dispatches this for the page of rows.
     */
    searchTranslations(_context, payload) {
      const { filter, offset, limit, callback } = payload;
      translationList(filter, offset, limit)
        .then((response) => {
          callback(response);
        })
        .catch((err) => {
          logger.error(err);
        });
    },

    executeSelectTranslation({ commit }, payload) {
      const { id, callback } = payload;
      getTranslation(id)
        .then((row) => {
          commit("saveTranslation", row);
          if (callback) {
            callback();
          }
        })
        .catch((err) => {
          logger.error(err);
        });
    },

    async saveTranslation(_context, payload) {
      const { translation, callback } = payload;
      try {
        if (!translation.id) {
          await createTranslation(translation);
          Notify.create({
            timeout: 5000,
            actions: [{ icon: "close", color: "white" }],
            message: i18n.global.t("translation.dialog.Addsuccess"),
            color: "positive",
          });
        } else {
          await updateTranslation(translation);
          Notify.create({
            timeout: 5000,
            actions: [{ icon: "close", color: "white" }],
            message: i18n.global.t("translation.dialog.Editsuccess"),
            color: "positive",
          });
        }
        if (callback) {
          callback();
        }
      } catch (err) {
        logger.error(err);
      }
    },

    async deleteTranslation(_context, payload) {
      try {
        await deleteTranslationApi(payload.id);
        Notify.create({
          timeout: 5000,
          actions: [{ icon: "close", color: "white" }],
          message: i18n.global.t("translation.dialog.Deletesuccess"),
          color: "positive",
        });
      } catch (err) {
        logger.error(err);
      }
    },
  },

  mutations: {
    setMode(state, payload) {
      state.mode = payload;
    },
    saveTranslation(state, payload) {
      state.selectedTranslation = payload;
    },
    initTranslationModule(state) {
      const initGetter = store.getters["translationModule/getTranslationSelectedInit"];
      state.selectedTranslation = initGetter;
      state.mode = "add";
    },
  },
};

export default translationModule;
